import { getPlans } from 'client/actions/subscriptionManagement'
import { initializeUserflow } from 'client/actions/userflow'
import { addBlockedAccountAnalytics } from 'client/components/Modal/FailedPaymentModals/AnalyticsFailedPaymentModal'
import { trackingFields } from 'client/Global/AnalyticsEvents'
import { type CollectionInterface } from 'client/Interfaces/CollectionInterface'
import { Translation } from 'client/Other/Translation'
import * as React from 'react'
import { Component } from 'react'
import { connect } from 'react-redux'
import { type RouteComponentProps, withRouter } from 'react-router-dom'

import { loadAppVitals } from './actions/app'
import { initializeBraze } from './actions/braze'
import { initializeDataDog } from './actions/dataDog'
import { navigateAddModal, navigateRemoveModal, setNavigation } from './actions/navigation'
import './App.scss'
import { signupDelay } from './components/AgerasAccounting/helpers'
import AppLoader from './components/common/AppLoader'
import { Menu } from './components/Menu'
import WelcomeModal from './components/Modal/WelcomeModal'
import { isCountryInEurope, isVATForced } from './components/Subscription/subscriptionHelperFunctions'
import { Segment } from './Global/Segment'
import { SegmentEvents } from './Global/SegmentEvents'
import { type CompanyInterface } from './Interfaces/CompanyInterface'
import { type NavigationInterface } from './Interfaces/NavigationInterface'
import { type SettingsInterface } from './Interfaces/Settings/SettingsInterface'
import { type StateInterface } from './Interfaces/StateInterface'
import {
  StripeVatValidation,
  type SubscriptionInterface,
  type SubscriptionPlanInterface,
} from './Interfaces/Subscription'
import './Other/POTools'
import { getCompanySubscriptionSelector } from './reducers/Subscription/companySubscription'

interface IStateProps {
  settings: SettingsInterface
  appLoading: any
  subscription: SubscriptionInterface
  availablePlans?: CollectionInterface<SubscriptionPlanInterface>
  company: CompanyInterface
}

interface AppStateProps {
  isLoading: boolean
}

interface IActionProps {
  loadAppVitals: () => Promise<void>
  initializeBraze: () => Promise<void>
  initializeUserflow: () => Promise<void>
  initializeDataDog: () => Promise<void>
  getPlans: () => Promise<void>
  setNavigation: (navigation: NavigationInterface) => void
  openFailedPaymentModal: () => void
  showPaymentMethodModal: () => void
  openDirectCheckoutModal: (selectedPlan: SubscriptionPlanInterface) => void
  openVATValidationModal: (subscription: SubscriptionInterface) => void
}

interface IExtProps {
  navigation: NavigationInterface
  children?: JSX.Element | JSX.Element[]
}

type AppProps = IActionProps & IStateProps & IExtProps & RouteComponentProps

export class App extends Component<AppProps, AppStateProps> {
  constructor(props: AppProps) {
    super(props)
    this.state = {
      isLoading: props.appLoading,
    }
  }

  public async componentDidMount() {
    await this.props.loadAppVitals()
    Translation.missingTranslationKeysLogger()

    if (this.props.company && this.props.settings) {
      await this.props.initializeBraze()
      await this.props.initializeUserflow()
      await this.props.initializeDataDog()
    }
    if (this.shouldShowPaymentCheckoutModal()) {
      await this.props.getPlans()
      this.openCheckoutPage()
    } else if (this.shouldShowFailedPaymentModal()) {
      this.props.openFailedPaymentModal()
    } else if (this.isPaymentMethodMissing() && !this.isPlanFree()) {
      this.props.showPaymentMethodModal()
    }
    if (this.shouldShowVatValidationModal()) {
      this.props.openVATValidationModal(this.props.subscription)
    }

    if (typeof this.props.history.listen === 'function') {
      this.unlisten = this.props.history?.listen(this.handleRouteChange)
    }

    if (window.localStorage.WelcomeModal) {
      // Set delay here as Segment is not ready on vitals loading
      this.setState({ isLoading: true })

      Segment.track(SegmentEvents.experiments.welcomeModal + 'viewed', { exp3_welcome_modal: 'control' })

      await signupDelay(this.props.company)
      this.setState({ isLoading: false })
    }
  }

  componentWillUnmount() {
    this.unlisten()
  }

  private unlisten() {}

  private handleRouteChange(location) {
    const locationArray = location.pathname.split('/')
    if (new Set(locationArray).size === locationArray.length) {
      const isBankingRoute = location?.pathname?.includes('banking')
      if (isBankingRoute) {
        Segment.page(location?.pathname === '/banking' ? 'banking' : 'banking/id')
      } else {
        Segment.page(
          location?.pathname
            ?.split('/')
            ?.filter((el) => el && isNaN(el))
            ?.join('/'),
        )
      }
    }
  }

  public UNSAFE_componentWillReceiveProps(props) {
    if (props?.ongoing?.SET_NAVIGATION) {
      return
    }

    if (
      props.history.location.pathname !== props.navigation?.path &&
      props.history.location.pathname !== `/${props.navigation?.path}`
    ) {
      this.props.history.push(props.navigation?.path)
    }
  }

  private shouldShowPaymentCheckoutModal() {
    const planId = window.localStorage.getItem('directCheckoutPlanId')
    return Boolean(planId)
  }

  private shouldShowVatValidationModal() {
    const subscriptionStatus = this.props.subscription?.status
    const isNotVATLiable = this.props.settings?.values?.company?.isNotVATLiable
    const vatNumberValidation = this.props.company?.vatNumberValidation
    const vatNumber = this.props.company?.vatNumber
    const country = this.props.company?.country
    const showModal =
      subscriptionStatus === 'active' &&
      ['INVALID', 'UNKNOWN'].includes(vatNumberValidation) &&
      vatNumber &&
      !isNotVATLiable &&
      isCountryInEurope(country) &&
      !isVATForced(country) &&
      this.props.subscription?.stripeVatValidation !== StripeVatValidation.verified
    return showModal
  }

  private isPaymentMethodMissing() {
    const subscription: SubscriptionInterface = this.props.subscription
    return subscription?.payment && subscription.payment.type === 'no_payment_method'
  }

  private isPlanFree() {
    return this.props.subscription.plan.name === 'plan.free'
  }

  private shouldShowFailedPaymentModal() {
    const subscription: SubscriptionInterface = this.props.subscription
    return (
      subscription?.payment && (Boolean(subscription.payment.disputesInfo) || Boolean(subscription.payment.dueInvoices))
    )
  }

  private openCheckoutPage() {
    const planId = window.localStorage.getItem('directCheckoutPlanId')
    const selectedPlan = this.props.availablePlans?.collection.find((plan) => plan.id === planId)
    window.localStorage.setItem('WelcomeModal', selectedPlan ? 'false' : 'true')
    window.localStorage.removeItem('directCheckoutPlanId')
    return selectedPlan ? this.props.openDirectCheckoutModal(selectedPlan) : null
  }

  public render() {
    const { appLoading, company } = this.props
    const showWelcomeModal = window.localStorage.WelcomeModal || false

    if (appLoading && !showWelcomeModal) {
      return <AppLoader />
    }

    return (
      <div id="app" className="ReactBased">
        <WelcomeModal isLoading={this.state.isLoading || appLoading} isShow={showWelcomeModal} />
        {!appLoading && <Menu />}
        {!appLoading && this.props.children}
      </div>
    )
  }
}

const mapStateToProps = (state: StateInterface): IStateProps => {
  const { ongoing, settings } = state
  return {
    settings,
    appLoading: ongoing.LOAD_APP_VITALS || false,
    subscription: getCompanySubscriptionSelector(state),
    availablePlans: state.subscriptionManagement.availablePlans,
    company: state.company,
  }
}

const mapDispatchToProps = (dispatch): IActionProps => ({
  loadAppVitals: async () => void (await dispatch(loadAppVitals())),
  initializeUserflow: async () => void (await dispatch(initializeUserflow())),
  initializeBraze: async () => void (await dispatch(initializeBraze())),
  initializeDataDog: async () => void (await dispatch(initializeDataDog())),
  getPlans: () => void dispatch(getPlans()),
  setNavigation: (navigation) => void dispatch(setNavigation(navigation)),
  openDirectCheckoutModal: (selectedPlan: SubscriptionPlanInterface) =>
    void dispatch(
      navigateAddModal({
        component: 'DirectCheckoutModal',
        props: {
          isOpen: true,
          selectedPlan,
          onClose: () => {
            dispatch(navigateRemoveModal('DirectCheckoutModal'))
            window.localStorage.removeItem('directCheckoutPlanId')
          },
        },
      }),
    ),
  showPaymentMethodModal: () =>
    void dispatch(
      navigateAddModal({
        component: 'UpdatePaymentDetailsModal',
        props: {
          isOpen: true,
          openFromDashboard: true,
        },
      }),
    ),
  openFailedPaymentModal: () => {
    addBlockedAccountAnalytics(trackingFields.Modifiers.open)
    void dispatch(
      navigateAddModal({
        component: 'FailedPaymentModal',
        props: {
          isOpen: true,
        },
      }),
    )
  },
  openVATValidationModal: (subscription: SubscriptionInterface) =>
    void dispatch(
      navigateAddModal({
        component: 'VATValidationModal',
        props: {
          isOpen: true,
          subscription,
          onClose: () => {
            dispatch(navigateRemoveModal('VATValidationModal'))
          },
        },
      }),
    ),
})

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App)

export default withRouter(ConnectedApp)
